/**
 * Counter — 100 numbered balls (1–100). Touch them in order; wrong ball or timeout = lose.
 * +1 score per correct ball. After clearing the field, 2s pause then 100 new balls respawn.
 */

export const COUNTER_BALLS_TOTAL = 100;
export const COUNTER_BALL_SPEED = 0.85;
export const COUNTER_ROUND_MS = 5000;
export const COUNTER_RESPAWN_DELAY_MS = 2000;

/** Bright gold for the ball you must touch next. */
export const COUNTER_TARGET_BALL_HEX = "#ffd84d";
/** Muted fill for all other numbered balls. */
export const COUNTER_IDLE_BALL_HEX = "#3d6670";

let roundMs = COUNTER_ROUND_MS;
let respawnDelayMs = COUNTER_RESPAWN_DELAY_MS;

export function setCounterTuning(opts?: { roundMs?: number; respawnDelayMs?: number }): void {
    roundMs =
        opts?.roundMs != null && Number.isFinite(opts.roundMs)
            ? Math.max(200, Math.floor(opts.roundMs))
            : COUNTER_ROUND_MS;
    respawnDelayMs =
        opts?.respawnDelayMs != null && Number.isFinite(opts.respawnDelayMs)
            ? Math.max(0, Math.floor(opts.respawnDelayMs))
            : COUNTER_RESPAWN_DELAY_MS;
}

function counterRoundMs(): number {
    return roundMs;
}

function counterRespawnDelayMs(): number {
    return respawnDelayMs;
}

let nextExpectedNum = 1;
let roundEndsAt = 0;
let respawnScheduledAt = 0;
let nextDisplay = 5;
let pulseUntil = 0;
let frozenRemainingMs = counterRoundMs();

function clamp01(v: number) {
    return Math.max(0, Math.min(1, v));
}

function syncDisplayFromRemaining(remainingMs: number, now: number) {
    const display = Math.ceil(Math.max(0, remainingMs) / 1000);
    if (display !== nextDisplay) {
        nextDisplay = display;
        pulseUntil = now + 180;
    }
}

export function resetCounterState(now: number) {
    nextExpectedNum = 1;
    roundEndsAt = now + counterRoundMs();
    respawnScheduledAt = 0;
    frozenRemainingMs = counterRoundMs();
    nextDisplay = 5;
    pulseUntil = now + 180;
}

export function resetCounterTimer(now: number) {
    roundEndsAt = now + counterRoundMs();
    frozenRemainingMs = counterRoundMs();
    nextDisplay = 5;
    pulseUntil = now + 180;
}

export function resetCounterWave(now: number) {
    nextExpectedNum = 1;
    respawnScheduledAt = 0;
    resetCounterTimer(now);
}

export function getCounterNextExpected() {
    return nextExpectedNum;
}

export function advanceCounterExpected() {
    nextExpectedNum += 1;
}

export function scheduleCounterRespawn(now: number) {
    if (respawnScheduledAt > 0) return;
    frozenRemainingMs = counterRoundMs();
    respawnScheduledAt = now + counterRespawnDelayMs();
    resetCounterTimer(now);
}

export function isCounterAwaitingRespawn(now: number) {
    return respawnScheduledAt > 0 && now < respawnScheduledAt;
}

/** Returns true once when the respawn delay has elapsed. */
export function tickCounterRespawn(now: number) {
    if (respawnScheduledAt <= 0 || now < respawnScheduledAt) return false;
    return true;
}

/** Returns true when the round timer hits zero (not while frozen). */
export function tickCounterTimer(now: number, frozen: boolean): boolean {
    if (roundEndsAt <= 0) return false;
    if (frozen) {
        syncDisplayFromRemaining(frozenRemainingMs, now);
        return false;
    }
    const remaining = roundEndsAt - now;
    frozenRemainingMs = Math.max(0, remaining);
    syncDisplayFromRemaining(remaining, now);
    return remaining <= 0;
}

export function getCounterTimerDisplay() {
    return nextDisplay;
}

export function getCounterHudRemainingMs(now: number, paused: boolean) {
    if (paused || isCounterAwaitingRespawn(now)) return frozenRemainingMs;
    return Math.max(0, roundEndsAt - now);
}

export function isCounterTimerPulsing(now: number) {
    return now < pulseUntil;
}

export function getCounterTimeFraction(now: number, paused = false) {
    return clamp01(getCounterHudRemainingMs(now, paused) / counterRoundMs());
}

/** Extend future deadlines after an unpaused pause (same pattern as Roulette). */
export function shiftCounterPauseTimers(deltaMs: number) {
    if (deltaMs <= 0) return;
    roundEndsAt = roundEndsAt > 0 ? roundEndsAt + deltaMs : roundEndsAt;
    respawnScheduledAt = respawnScheduledAt > 0 ? respawnScheduledAt + deltaMs : respawnScheduledAt;
    pulseUntil = pulseUntil > 0 ? pulseUntil + deltaMs : pulseUntil;
}

export function isCounterTargetBall(num: number) {
    return num === nextExpectedNum;
}

export function getCounterBallHex(num: number) {
    if (isCounterTargetBall(num)) return COUNTER_TARGET_BALL_HEX;
    return COUNTER_IDLE_BALL_HEX;
}

export function getCounterBallDrawAlpha(num: number) {
    return isCounterTargetBall(num) ? 1 : 0.48;
}

export function getCounterBallNeonStrength(num: number) {
    return isCounterTargetBall(num) ? 1 : 0.22;
}

export function drawCounterBallLabel(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    radius: number,
    num: number,
    now = 0,
) {
    const highlighted = isCounterTargetBall(num);
    const label = String(num);
    const baseFont = highlighted
        ? Math.min(radius * 1.02, label.length >= 3 ? radius * 0.64 : radius * 0.78)
        : Math.max(5, Math.min(radius * 0.92, label.length >= 3 ? radius * 0.58 : radius * 0.72));
    const pulse = highlighted ? 1 + Math.sin(now * 0.012) * 0.08 : 1;
    const fontPx = baseFont * pulse;

    ctx.save();
    ctx.font = `bold ${fontPx}px 'Press Start 2P', monospace`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.lineWidth = Math.max(1, fontPx * (highlighted ? 0.2 : 0.14));

    if (highlighted) {
        ctx.shadowColor = "#fff8a8";
        ctx.shadowBlur = Math.max(8, radius * 1.1);
        ctx.strokeStyle = "rgba(120,70,0,0.9)";
        ctx.fillStyle = "#fffef5";
    } else {
        ctx.shadowBlur = 0;
        ctx.strokeStyle = "rgba(0,0,0,0.75)";
        ctx.fillStyle = "rgba(210,225,230,0.82)";
    }

    ctx.strokeText(label, x, y);
    ctx.fillText(label, x, y);
    ctx.restore();
}

/** Bottom time bar + small countdown above it (Roulette-style digits). */
export function drawCounterTimeHud(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    layoutScale: number,
    now: number,
    paused = false,
) {
    if (w < 40 || h < 40) return;

    const remainingMs = getCounterHudRemainingMs(now, paused);
    const displaySec = Math.ceil(remainingMs / 1000);
    const margin = Math.max(14, 16 * layoutScale);
    const barH = Math.max(8, 10 * layoutScale);
    const barW = Math.min(w * 0.74, w - margin * 2);
    const barX = (w - barW) * 0.5;
    const barY = h - margin - barH;
    const frac = clamp01(remainingMs / counterRoundMs());
    const r = Math.min(barH * 0.45, 5 * layoutScale);

    ctx.save();

    ctx.fillStyle = "rgba(0,0,0,0.45)";
    ctx.beginPath();
    ctx.roundRect(barX, barY, barW, barH, r);
    ctx.fill();

    if (frac > 0) {
        const fillW = Math.max(barH, barW * frac);
        const grad = ctx.createLinearGradient(barX, 0, barX + barW, 0);
        grad.addColorStop(0, "#66ffff");
        grad.addColorStop(0.55, "#ffffff");
        grad.addColorStop(1, frac < 0.25 ? "#ff5555" : "#aaffff");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.roundRect(barX, barY, fillW, barH, r);
        ctx.fill();
    }

    ctx.strokeStyle = "rgba(255,255,255,0.35)";
    ctx.lineWidth = Math.max(1, 1.2 * layoutScale);
    ctx.beginPath();
    ctx.roundRect(barX, barY, barW, barH, r);
    ctx.stroke();

    const pulse = isCounterTimerPulsing(now);
    const timerSize = (pulse ? 26 : 22) * layoutScale;
    const timerY = barY - Math.max(12, 14 * layoutScale);
    ctx.font = `${timerSize}px 'Press Start 2P', monospace`;
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.fillStyle = "#ffffff";
    ctx.shadowColor = "rgba(255,255,255,0.55)";
    ctx.shadowBlur = pulse ? 14 : 8;
    ctx.fillText(String(displaySec), w * 0.5, timerY);
    ctx.shadowBlur = 0;

    ctx.restore();
}
